import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Zap, ArrowRight, CheckCircle, Calendar, TrendingUp, Clock, Shield, Target, Users, LayoutGrid, Database, Sliders, MessageSquare, Phone, Brain, Search, GitBranch, Heart } from 'lucide-react';

// Import Assets
import amyAvatar from '../assets/amy-avatar.png';

const ScheduleOptimizerPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Leena, Your AI Schedule Optimizer</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">The World's Only Dental AI Schedule</span><br />
                <span className="text-blue-600">Optimizer</span>
              </h1>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-lg">
                Leena proactively fills gaps in your schedule by contacting waitlisted patients and moving appointments forward. She ensures your chairs are always full and your day is optimized for maximum production.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 shadow-sm">
                    Get a demo
                  </button>
                </Link>
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              {/* Main Profile with Card */}
              <div className="relative">
                {/* Main Image Card with Gradient Glow */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-purple-400 shadow-xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src="/assets/leena.png"
                      alt="Leena AI Optimizer"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator on ear */}
                  <div className="absolute top-32 right-12">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-cyan-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-cyan-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Audio waveform at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-md border border-gray-200">
                    <div className="flex items-center gap-1">
                      {[4, 8, 6, 10, 5, 9, 4, 7, 11, 6, 8, 5].map((height, i) => (
                        <div 
                          key={i} 
                          className="w-1 bg-blue-500 rounded-full"
                          style={{ height: `${height * 2}px` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble - top left */}
                <div className="absolute -top-4 -left-16 bg-white rounded-2xl p-4 shadow-md w-60 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">Hi John, we have an earlier opening for your crown prep this Tuesday at 10 AM. Would you like to grab it?</p>
                  {/* Tail */}
                  <div className="absolute bottom-6 right-0 w-3 h-3 bg-white transform rotate-45 translate-x-1.5 border-r border-b border-gray-200"></div>
                </div>
                
                {/* Response Assistant badge - bottom left */}
                <div className="absolute bottom-24 -left-12 bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">Smart Filling</span>
                  </div>
                </div>
                
                {/* Stats card - top right */}
                <div className="absolute top-4 -right-16 bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700">Utilization: 98%</span>
                  </div>
                </div>
                
                {/* Circular stats - bottom right */}
                <div className="absolute -bottom-8 -right-20 bg-white rounded-2xl p-4 shadow-md border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="relative w-16 h-16">
                      {/* Semi-circle graph */}
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray="98, 100"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700">98%</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-xs text-gray-500">Filled</span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700">Schedule Full</p>
                      <p className="text-xs text-gray-400">Zero Gaps</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Transformation Graphic */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  From Patchy to Packed
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  AI that turns a <br/>
                  <span className="text-blue-600">patchy schedule into a tight schedule.</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  For an average dental office that might have 100 appointments a week, carrying out this 12-step optimization workflow would mean staff would have to carry out 1,200 steps per week, which is why human staff could never do this whereas our AI schedule optimizer can.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/book-demo">
                  <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  View Pricing
                </button>
              </div>

            </div>

            {/* Right: Transformation Visual */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#0f172a] p-6 text-white border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">Schedule Optimization</h3>
                      <p className="text-gray-400 text-sm">Before & After</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Zap className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gray-50/50">
                  <div className="grid grid-cols-2 gap-4">
                    
                    {/* Before Column */}
                    <div className="space-y-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wide text-center mb-2">Before Leena</div>
                      
                      {/* Slot 1 - Unconfirmed */}
                      <div className="p-3 rounded-lg bg-gray-100 border border-gray-200 border-dashed opacity-70">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-gray-500">09:00</span>
                          <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                        </div>
                        <div className="h-1.5 w-16 bg-gray-300 rounded mb-1"></div>
                        <span className="text-[10px] text-gray-500 font-medium">Unconfirmed</span>
                      </div>

                      {/* Slot 2 - Empty Gap */}
                      <div className="p-3 rounded-lg bg-white border border-red-100 border-dashed">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-red-400">10:00</span>
                          <span className="text-[10px] font-bold text-red-500 uppercase">Empty</span>
                        </div>
                        <div className="h-8 flex items-center justify-center bg-red-50/50 rounded text-[10px] text-red-400">
                           No Revenue
                        </div>
                      </div>

                      {/* Slot 3 - Late Cancel */}
                      <div className="p-3 rounded-lg bg-red-50 border border-red-100 opacity-60">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-gray-500">11:00</span>
                          <span className="text-[10px] text-red-600 font-bold">Cancelled</span>
                        </div>
                        <div className="h-1.5 w-12 bg-red-200 rounded"></div>
                      </div>
                    </div>

                    {/* Arrow Divider (Absolute Center) - Optional, but simple border here */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center justify-center">
                      <div className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center border border-gray-100 text-blue-600">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>

                    {/* After Column */}
                    <div className="space-y-3">
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wide text-center mb-2">After Leena</div>

                      {/* Slot 1 - Confirmed */}
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 shadow-sm transform scale-105 transition-transform">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-blue-600 font-bold">09:00</span>
                          <CheckCircle className="w-3 h-3 text-blue-500" />
                        </div>
                        <div className="h-1.5 w-20 bg-blue-200 rounded mb-1"></div>
                        <span className="text-[10px] text-blue-700 font-bold">Confirmed</span>
                      </div>

                      {/* Slot 2 - Gap Filled */}
                      <div className="p-3 rounded-lg bg-green-50 border border-green-100 shadow-sm transform scale-105 transition-transform">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-green-700 font-bold">10:00</span>
                          <Zap className="w-3 h-3 text-green-500" />
                        </div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[10px] font-bold text-green-800">Gap Filled</span>
                        </div>
                         <div className="h-1.5 w-16 bg-green-200 rounded"></div>
                      </div>

                      {/* Slot 3 - Rescheduled */}
                      <div className="p-3 rounded-lg bg-purple-50 border border-purple-100 shadow-sm transform scale-105 transition-transform">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-mono text-purple-700 font-bold">11:00</span>
                          <Calendar className="w-3 h-3 text-purple-500" />
                        </div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[10px] font-bold text-purple-800">Retained</span>
                        </div>
                         <div className="h-1.5 w-14 bg-purple-200 rounded"></div>
                      </div>
                    </div>

                  </div>
                  
                  {/* Execution Stats */}
                  <div className="mt-6 pt-4 border-t border-gray-200/50">
                     <div className="flex justify-between text-center divide-x divide-gray-200">
                        <div className="px-2 flex-1">
                            <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wider mb-1">Utilization</div>
                            <div className="text-sm font-bold text-green-600 flex items-center justify-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                +24%
                            </div>
                        </div>
                         <div className="px-2 flex-1">
                            <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wider mb-1">Revenue</div>
                            <div className="text-sm font-bold text-blue-600 flex items-center justify-center gap-1">
                                <Zap className="w-3 h-3" />
                                Optimized
                            </div>
                        </div>
                     </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Smart Optimization Features */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Smart Optimization That <br/>
                    <span className="text-blue-600">Maximizes Production</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Leena constantly monitors your schedule for gaps, cancellations, and opportunities to move appointments forward. She works 24/7 to ensure your day is as productive as possible.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Multi-Channel Engagement */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-blue-100 via-purple-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         <div className="relative w-full max-w-md">
                            <div className="glass-card rounded-3xl p-6 relative z-20 bg-white/60 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Live Engagement Sequence</span>
                                    </div>
                                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold">Patient: Mike T.</span>
                                </div>

                                <div className="space-y-4 relative">
                                    {/* Vertical Line */}
                                    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200 z-0"></div>

                                    {/* Attempt 1: SMS */}
                                    <div className="relative z-10 flex gap-3 opacity-60">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-500">
                                            <MessageSquare className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="bg-white p-2.5 rounded-xl rounded-tl-none border border-gray-100 shadow-sm text-[10px] text-gray-500 w-full">
                                            <span className="font-bold block text-gray-400 text-[9px] mb-0.5">10:00 AM • SMS Sent</span>
                                            Hi Mike, we have an opening tomorrow at 2pm. Interested?
                                        </div>
                                    </div>

                                    {/* Attempt 2: Voice Call (Active) */}
                                    <div className="relative z-10 flex gap-3 transform translate-x-1">
                                        <div className="w-8 h-8 rounded-full bg-blue-600 border border-blue-500 flex items-center justify-center flex-shrink-0 text-white shadow-md animate-pulse">
                                            <Phone className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="bg-blue-600 p-3 rounded-xl rounded-tl-none shadow-lg text-white w-full">
                                            <span className="font-bold block text-blue-200 text-[9px] mb-1">10:45 AM • Voice Call Active</span>
                                            <p className="text-xs leading-snug">"Hey Mike! I noticed you missed my text. I really want to get that crown prep sorted for you before the weekend..."</p>
                                        </div>
                                    </div>

                                    {/* Outcome */}
                                    <div className="relative z-10 flex gap-3 pl-11">
                                        <div className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5 shadow-sm border border-green-200">
                                            <CheckCircle className="w-3 h-3" />
                                            Appointment Confirmed
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Phone className="w-4 h-4" />
                             MULTI-CHANNEL PURSUIT
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Persistent AI Voice & SMS Campaigns</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Leena doesn't just send one text. She intelligently pursues patients with <strong>human-like voice calls</strong> and <strong>conversational SMS</strong> until the appointment is confirmed, capturing revenue that basic reminders miss.
                        </p>
                    </div>
                </div>

                {/* Card 2: Strategic Rescheduling */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-indigo-100 via-blue-100 to-indigo-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         <div className="absolute top-10 left-10 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            <div className="bg-white/60 backdrop-blur-md rounded-3xl p-5 border border-white/50 shadow-xl space-y-4">
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 p-0.5 flex-shrink-0">
                                        <img src="/assets/leena.png" className="w-full h-full rounded-full object-cover" alt="Leena" />
                                    </div>
                                    <div className="bg-white p-3 rounded-2xl rounded-tl-none text-xs text-gray-700 shadow-sm border border-gray-100">
                                        Hi Sarah, Dr. Smith had a last-minute opening tomorrow at 2 PM. Since you're scheduled for next week, would you like to come in earlier?
                                    </div>
                                </div>

                                <div className="flex gap-3 flex-row-reverse">
                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs flex-shrink-0">P</div>
                                    <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-xs text-white shadow-sm">
                                        Yes, that works much better for me!
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 p-0.5 flex-shrink-0">
                                        <img src="/assets/leena.png" className="w-full h-full rounded-full object-cover" alt="Leena" />
                                    </div>
                                    <div className="bg-white p-3 rounded-2xl rounded-tl-none text-xs text-gray-700 shadow-sm border border-gray-100">
                                        Perfect! I've moved your appointment to tomorrow at 2 PM.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Calendar className="w-4 h-4" />
                             SMART MOVES
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Understands your scheduling rules to move up appointments</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Leena understands your complex scheduling rules and understands which appointments are eligible to be moved up versus not.
                        </p>
                    </div>
                </div>

                {/* Card 3: Patient Memory */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         <div className="absolute top-0 right-0 w-64 h-64 bg-green-200/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         <div className="relative w-full max-w-md z-10">
                            <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl space-y-4">
                                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                        <Brain className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-gray-500 uppercase">Context Memory</div>
                                        <div className="text-sm font-bold text-gray-900">Patient: John D.</div>
                                    </div>
                                </div>
                                
                                <div className="space-y-3">
                                    <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm opacity-60">
                                        <div className="text-[10px] text-gray-400 mb-1">2 Weeks Ago</div>
                                        <div className="text-xs text-gray-600">"I'm travelling to Europe until the 15th."</div>
                                    </div>
                                    
                                    <div className="flex justify-center">
                                        <ArrowRight className="w-4 h-4 text-gray-400 transform rotate-90" />
                                    </div>

                                    <div className="bg-green-50 p-3 rounded-xl border border-green-100 shadow-sm">
                                        <div className="text-[10px] text-green-600 mb-1 font-bold">Today (The 16th)</div>
                                        <div className="text-xs text-gray-800">"Hi John, welcome back! I recall you wanted to schedule that cleaning once you returned."</div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    <div>
                        <div className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Brain className="w-4 h-4" />
                             PATIENT MEMORY
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Never Forgets a Detail</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Leena remembers context across weeks. If a patient says they are travelling, she snoozes the outreach and calls them back exactly when they return with a personalized welcome message.
                        </p>
                    </div>
                </div>

                {/* Card 4: Sentiment Understanding */}
                <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         <div className="absolute top-10 left-10 w-40 h-40 bg-pink-200/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-rose-200/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl space-y-4">
                                <div className="flex flex-col gap-3">
                                    <div className="flex gap-3 items-start">
                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-xs font-bold text-gray-500">P</div>
                                        <div className="bg-white p-3 rounded-2xl rounded-tl-none text-xs text-gray-700 shadow-sm border border-gray-100">
                                            "I'm actually really busy with work right now, can you try me later?"
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 px-2">
                                        <div className="h-px bg-gray-300 flex-1"></div>
                                        <div className="text-[10px] font-bold text-pink-500 bg-pink-50 px-2 py-1 rounded-full border border-pink-100 flex items-center gap-1">
                                            <Heart className="w-3 h-3" /> Sentiment: Busy / Stressed
                                        </div>
                                        <div className="h-px bg-gray-300 flex-1"></div>
                                    </div>

                                    <div className="flex gap-3 flex-row-reverse items-start">
                                        <div className="w-8 h-8 rounded-full bg-pink-100 p-0.5 flex-shrink-0">
                                            <img src="/assets/leena.png" className="w-full h-full rounded-full object-cover" alt="Leena" />
                                        </div>
                                        <div className="bg-pink-600 p-3 rounded-2xl rounded-tr-none text-xs text-white shadow-sm">
                                            "No problem at all. I'll make a note to follow up in 2 weeks when things might be calmer. Have a good day!"
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Heart className="w-4 h-4" />
                             SENTIMENT ANALYSIS
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Reads Between the Lines</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Leena understands human sentiment. If a patient sounds stressed or busy, she backs off and reschedules the follow-up, preserving the relationship instead of being annoying.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* REFINED SECTION: The Engine Behind The Perfect Schedule (Visual Flow) */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-20 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The Engine Behind <br />
              <span className="text-blue-600">The Perfect Schedule</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Every appointment involves over 12 intelligent decision-making steps. Leena executes these instantly to keep your schedule packed.
            </p>
            
            <div className="inline-block bg-blue-50 border border-blue-100 rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
                <div className="bg-blue-600 text-white font-bold text-2xl px-4 py-2 rounded-lg">
                  1,200 Steps
                </div>
                <div className="text-blue-900">
                  <p className="font-bold text-lg">Per Week For Just 100 Appointments</p>
                  <p className="text-sm opacity-80">Impossible for humans. Instant for Leena.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative max-w-6xl mx-auto">
            {/* Background Grid for Structure */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:2rem_2rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20 pointer-events-none"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
                
                {/* Phase 1: Analyze & Detect */}
                <div className="space-y-6">
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-center">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Phase 1: Analysis</span>
                    </div>
                    
                    {/* Step 1 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-blue-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">1</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 text-blue-700">
                                <Search className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Scan Schedule</h4>
                            <p className="text-xs text-gray-500 leading-snug">Intelligently reviews 2-3 days ahead for gaps.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 2 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-blue-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">2</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 text-blue-700">
                                <Users className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Identify Candidates</h4>
                            <p className="text-xs text-gray-500 leading-snug">Finds waitlist patients matching the slot criteria.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 3 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-blue-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">3</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-3 text-blue-700">
                                <Sliders className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Check Rules</h4>
                            <p className="text-xs text-gray-500 leading-snug">Verifies insurance & provider match logic.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>
                </div>

                {/* Phase 2: Initial Outreach */}
                <div className="space-y-6">
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-center">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Phase 2: Outreach</span>
                    </div>

                    {/* Step 4 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-indigo-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">4</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center mb-3 text-indigo-700">
                                <Phone className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Initial Call</h4>
                            <p className="text-xs text-gray-500 leading-snug">Calls to confirm or fill slot with AI voice.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 5 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-indigo-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">5</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center mb-3 text-indigo-700">
                                <MessageSquare className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Smart SMS</h4>
                            <p className="text-xs text-gray-500 leading-snug">Sends conversational text if no answer.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 6 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-indigo-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">6</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center mb-3 text-indigo-700">
                                <Clock className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Timing Logic</h4>
                            <p className="text-xs text-gray-500 leading-snug">Determines best time to retry contact.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>
                </div>

                {/* Phase 3: Persistent Follow-up */}
                <div className="space-y-6">
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-center">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Phase 3: Persistence</span>
                    </div>

                    {/* Step 7 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-purple-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">7</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center mb-3 text-purple-700">
                                <Phone className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Follow Up Call</h4>
                            <p className="text-xs text-gray-500 leading-snug">Second attempt for unresponsive patients.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 8 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-purple-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">8</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center mb-3 text-purple-700">
                                <Zap className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Handle Cancel</h4>
                            <p className="text-xs text-gray-500 leading-snug">Manages incoming cancellation requests.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>

                    {/* Step 9 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-purple-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">9</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center mb-3 text-purple-700">
                                <Brain className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Reschedule</h4>
                            <p className="text-xs text-gray-500 leading-snug">Negotiates new time to retain patient.</p>
                        </div>
                        <div className="hidden lg:block absolute top-1/2 -right-4 w-4 h-0.5 bg-gray-200"></div>
                    </div>
                </div>

                {/* Phase 4: Resolution */}
                <div className="space-y-6">
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-center">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Phase 4: Resolution</span>
                    </div>

                    {/* Step 10 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-green-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">10</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center mb-3 text-green-700">
                                <Users className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Gap Filling</h4>
                            <p className="text-xs text-gray-500 leading-snug">Finds new patient for opened slot.</p>
                        </div>
                        <div className="hidden lg:block absolute bottom-0 left-1/2 w-0.5 h-6 bg-gray-200"></div>
                    </div>

                    {/* Step 11 */}
                    <div className="group bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:border-green-300 relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">11</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center mb-3 text-green-700">
                                <CheckCircle className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">Confirmation</h4>
                            <p className="text-xs text-gray-500 leading-snug">Secures agreement from patient.</p>
                        </div>
                        <div className="hidden lg:block absolute bottom-0 left-1/2 w-0.5 h-6 bg-gray-200"></div>
                    </div>

                    {/* Step 12 */}
                    <div className="group bg-gray-900 p-5 rounded-xl shadow-lg border border-gray-800 hover:shadow-xl transition-all relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">12</div>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center mb-3 text-green-400 backdrop-blur-sm">
                                <Database className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-white text-sm mb-1">Update PMS</h4>
                            <p className="text-xs text-gray-400 leading-snug">Writes back to schedule instantly.</p>
                        </div>
                    </div>
                </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  MAXIMIZE UTILIZATION
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  Harbor Hill Dentistry saw a $7,000/month increase in production.
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  By letting Leena manage their schedule, they filled empty gaps automatically. Stop letting empty chairs eat your profits.
                </p>
                
                <Link to="/book-demo">
                  <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                    <span className="flex items-center gap-2">
                      Book a Demo
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/10 rounded-full"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live Agent</span>
                    </div>
                    <span className="text-xs font-mono text-gray-400">00:05</span>
                  </div>

                  <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-3">
                      <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100">
                        <img
                          src="/assets/leena.png"
                          alt="Leena"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <div className="bg-blue-600 rounded-full p-1">
                          <Zap className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Leena</h3>
                    <p className="text-sm text-blue-600 font-medium">Schedule Optimizer</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Filling Gaps</span>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Optimizing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ScheduleOptimizerPage;
